import { useToast } from '@chakra-ui/react'
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Access } from '../Enuns/access.enum'
import { CookieInfo } from '../Enuns/cookie.enum'
import { User } from '../Interfaces/users'
import { api } from '../services/api'
import { cookies } from '../utils/cookies'

type Credentials = {
  username: string
  password: string
}

export type AuthPermission = string

type AuthContextValues = {
  signOut: () => void
  signIn: (credentials: Credentials) => Promise<boolean>
  isAuthenticated: boolean
  user: User | undefined
  permissions: AuthPermission[]
  isAdmin: boolean
  hasPermission: (permission: AuthPermission | AuthPermission[]) => boolean
  hasAnyPermission: (permissionsToCheck: AuthPermission[]) => boolean
  hasAllPermissions: (permissionsToCheck: AuthPermission[]) => boolean
}

type Props = {
  children: ReactNode
}

export const AuthContext = createContext({} as AuthContextValues)

const storedUser = cookies.get('@AXAuth:User') as User | undefined
const storedPermissionsFromCookies = cookies.get('@AXAuth:Permissions') || []
const storedToken = cookies.get('@AXAuth:Token') as string | undefined

export const AuthProvider = ({ children }: Props) => {
  const [user, setUser] = useState<User | undefined>(storedUser)
  const [permissions, setPermissions] = useState<AuthPermission[]>(
    Array.isArray(storedPermissionsFromCookies) ? storedPermissionsFromCookies : [],
  )

  const toast = useToast()

  const persistSession = (token: string, userData: User, resolvedPermissions: AuthPermission[]) => {
    cookies.set('@AXAuth:Token', token, { path: '/', maxAge: CookieInfo.MAX_AGE })
    cookies.set('@AXAuth:User', userData, {
      path: '/',
      maxAge: CookieInfo.MAX_AGE,
    })
    cookies.set('@AXAuth:Permissions', resolvedPermissions, {
      path: '/',
      maxAge: CookieInfo.MAX_AGE,
    })
  }

  useEffect(() => {
    if (storedToken) {
      api.defaults.headers['Authorization'] = `Bearer ${storedToken}`
    }
  }, [])

  const signIn = async ({ username, password }: Credentials) => {
    try {
      const response = await api.post('/login', {
        username,
        password,
      })

      const currentUser = response.data.user as User
      const resolvedPermissions = (response.data.permissions || []) as AuthPermission[]

      const token = response.data.token.token

      setUser(currentUser)
      setPermissions(resolvedPermissions)

      persistSession(token, currentUser, resolvedPermissions)

      api.defaults.headers['Authorization'] = `Bearer ${token}`

      return true
    } catch (err) {
      if (err.response) {
        toast({ description: err.response.data.error, status: 'error' })
      }

      return false
    }
  }

  const signOut = async () => {
    try {
      await api.post('/logout')
    } catch (error) {
      // ignore logout errors, session will be cleared locally
    }

    setUser(undefined)
    setPermissions([])

    cookies.remove('@AXAuth:Token', { path: '/' })
    cookies.remove('@AXAuth:User', { path: '/' })
    cookies.remove('@AXAuth:Permissions', { path: '/' })
  }

  const isAdmin = useMemo(() => {
    if (!user?.access) return false

    const normalized = String(user.access).toUpperCase()
    return normalized === 'ADMIN' || normalized === Access.ADMIN.toUpperCase()
  }, [user])

  const hasPermission = (permissionToCheck: AuthPermission | AuthPermission[]) => {
    if (isAdmin) return true

    const list = Array.isArray(permissionToCheck) ? permissionToCheck : [permissionToCheck]

    if (!list.length) return true

    return list.every((permission) => {
      const options = permission.split('|').map((item) => item.trim())
      return options.some((option) => permissions.includes(option))
    })
  }

  const hasAnyPermission = (permissionsToCheck: AuthPermission[]) => {
    if (isAdmin) return true

    if (!permissionsToCheck?.length) return true

    return permissionsToCheck.some((permission) => hasPermission(permission))
  }

  const hasAllPermissions = (permissionsToCheck: AuthPermission[]) => {
    if (isAdmin) return true

    if (!permissionsToCheck?.length) return true

    return permissionsToCheck.every((permission) => hasPermission(permission))
  }

  return (
    <AuthContext.Provider
      value={{
        signOut,
        signIn,
        isAuthenticated: !!user,
        user,
        permissions,
        isAdmin,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
